<template>
  <div>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader>
            <strong style="line-height: 2.3">병해충 목록</strong>
            <div class="card-header-actions">
              <CButton
                type="button"
                size="sm"
                color="outline-primary"
                to="/analysis/disease/info/add"
              >
                <i class="fa fa-plus" aria-hidden="true"></i>
                추가
              </CButton>
            </div>
          </CCardHeader>
          <CCardBody>
            <vue-good-table
              mode="remote"
              @on-page-change="onPageChange"
              @on-sort-change="onSortChange"
              @on-column-filter="onColumnFilter"
              @on-per-page-change="onPerPageChange"
              @on-row-click="onRowClick"
              :totalRows="totalRecords"
              :isLoading.sync="isLoading"
              :pagination-options="paginationOptions"
              :sort-options="sortOptions"
              :rows="rows"
              :columns="columns"
              styleClass="vgt-table"
              compactMode
            >
            </vue-good-table>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { VueGoodTable } from "vue-good-table";
import core from "@/core";
import DiseaseService from "@/services/disease/disease.service";
import CodeService from "@/services/code/code.service";

export default {
  name: "DiseaseInfo",
  components: {
    VueGoodTable,
  },
  data() {
    return {
      isLoading: false,
      paginationOptions: {
        enabled: true,
        perPage: 10,
        mode: "pages",
        position: "bottom",
        perPageDropdown: [10, 20, 50],
        dropdownAllowAll: false,
        setCurrentPage: 1,
        nextLabel: "다음",
        prevLabel: "이전",
        rowsPerPageLabel: "페이지 당 행 수",
        pageLabel: "페이지", // for 'pages' mode
        ofLabel: "/",
        allLabel: "All",
      },
      sortOptions: {
        enabled: true,
        initialSortBy: { field: "id", type: "desc" },
      },
      columns: [
        {
          label: "ID",
          field: "id",
          width: "60px",
        },
        {
          label: "작물",
          field: "cropCode",
          width: "70px",
          formatFn: this.renderCropCode,
        },
        {
          label: "생성시간",
          field: "createdAt",
          width: "110px",
        },
        {
          label: "수정시간",
          field: "updatedAt",
          width: "110px",
        },
        {
          label: "농장주명",
          field: "farmerName",
          width: "110px",
          sortable: false,
        },
        {
          label: "주소",
          field: this.renderAddress,
          width: "150px",
          sortable: false,
        },
        {
          label: "병해명",
          field: "diseaseName",
          width: "140px",
        },
        {
          label: "병해단계",
          field: "diseaseStep",
          width: "100px",
          formatFn: this.renderDiseaseStep,
        },
        {
          label: "작물상 위치",
          field: "cropPosition",
          width: "120px",
          formatFn: this.renderCropPosition,
        },
        {
          label: "온실내 위치",
          field: "housePosition",
          width: "120px",
        },
        {
          label: "측정날짜",
          field: "date",
          width: "100px",
          formatFn: this.render,
        },
        {
          label: "측정온도",
          field: "temp",
          width: "100px",
          formatFn: this.render,
        },
        {
          label: "측정습도",
          field: "humidity",
          width: "100px",
          formatFn: this.render,
        },
      ],
      rows: [],
      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: {
          field: "",
          type: "",
        },
        page: 1,
        perPage: 10,
      },
      cropCodeList: [],
    };
  },
  mounted() {
    this.$nextTick(async () => {
      this.cropCodeList = await CodeService.getCropCodeList();
      core.loader.hide();
    });
  },
  methods: {
    render(value) {
      // console.log("value : ", value);
      if (value == null) return "";
      return value;
    },
    renderCropCode(value) {
      let cropName = "";
      this.cropCodeList.some((cropCode) => {
        if (value === cropCode.code) {
          cropName = cropCode.koName;
          return true;
        }
      });
      return cropName;
    },
    renderDiseaseStep(value) {
      if (value === "1") return "초기";
      else if (value === "2") return "중기";
      else if (value === "3") return "말기";
      return "";
    },
    renderCropPosition(value) {
      if (value === "1") return "꽃";
      else if (value === "2") return "잎";
      else if (value === "3") return "과실";
      else if (value === "4") return "뿌리";
      return "";
    },
    renderAddress(item) {
      //console.log("item : ", item);
      if (item.address2 != null) {
        return `${item.address1} ${item.address2}`;
      }
      return item.address1;
    },
    onRowClick(params) {
      const item = params.row;
      //console.log("item : ", item);
      this.$router.push({
        path: `/analysis/disease/info/edit`,
        query: {
          id: item.id,
        },
      });
    },
    updateParams(newProps) {
      //console.log("updateParams : ", newProps);
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },

    onPageChange(params) {
      //console.log("onPageChange : ", params);
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      //console.log("onPerPageChange : ", params);
      this.updateParams({ perPage: params.currentPerPage });
      this.loadItems();
    },

    onSortChange(params) {
      //console.log("params : ", params);
      this.updateParams({
        sort: params,
      });
      this.loadItems();
    },

    onColumnFilter(params) {
      //console.log("onColumnFilter : ", params);
      this.updateParams(params);
      this.loadItems();
    },

    // load items is what brings back the rows from server
    async loadItems() {
      //console.log(this.serverParams);
      const sort = this.serverParams.sort[0];

      const request = {
        start: (this.serverParams.page - 1) * this.serverParams.perPage,
        length: this.serverParams.perPage,
        order: sort.type,
        orderColumnName: sort.field,
      };
      const response = await DiseaseService.getInfoTableList(request);
      this.rows = response.data;
      this.totalRecords = response.recordsTotal;
      //console.log("response : ", response);
    },
  },
};
</script>
